import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs
} from '@nextui-org/react';
import { collection, deleteDoc, doc, getDocs, limit as fsLimit, orderBy, query, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';

// Agencias que han gastado el 80 % (o más) de los créditos de su plan. Las
// crea el Worker al asignar una licencia (agency_credit_alerts, una por
// agencia y tope del plan) y refresca el consumo en cada asignación
// posterior; retirar licencias no lo baja. Aquí se revisan y se marca su
// estado (p. ej. tras ofrecerles más créditos o subir de plan).

const FETCH_LIMIT = 500;

const ALERT_STATUS = [
  { key: 'new', label: 'Nuevo', plural: 'Nuevos', color: 'warning' },
  { key: 'contacted', label: 'Contactada', plural: 'Contactadas', color: 'success' },
  { key: 'discarded', label: 'Descartado', plural: 'Descartados', color: 'default' }
];
const STATUS_BY_KEY = Object.fromEntries(ALERT_STATUS.map((s) => [s.key, s]));

const PLAN_LABEL = { rookie: 'Rookie', silver: 'Silver', gold: 'Gold', titanium: 'Titanium' };
const PLAN_COLOR = { rookie: 'default', silver: 'default', gold: 'warning', titanium: 'primary' };

function formatTs(ts) {
  const d = ts?.toDate?.();
  return d ? d.toLocaleString() : '—';
}

function pctColor(pct) {
  if (pct >= 100) return 'danger';
  if (pct >= 90) return 'warning';
  return 'primary';
}

export default function AgenciaAvisosCreditos() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(query(collection(db, 'agency_credit_alerts'), orderBy('createdAt', 'desc'), fsLimit(FETCH_LIMIT)));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('[AvisosCreditos] load:', e);
      setError(e.message || 'Error cargando avisos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const acc = { all: items.length, new: 0, contacted: 0, discarded: 0 };
    for (const it of items) if (acc[it.status] !== undefined) acc[it.status]++;
    return acc;
  }, [items]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((it) =>
      (statusFilter === 'all' || it.status === statusFilter) &&
      (!needle || [it.agencyName, it.agencyId, it.plan, it.triggeredByName].some((v) => String(v || '').toLowerCase().includes(needle))));
  }, [items, statusFilter, search]);

  async function setStatus(it, status) {
    if (!status || status === it.status) return;
    const prev = it.status;
    setItems((list) => list.map((x) => (x.id === it.id ? { ...x, status } : x)));
    try {
      await updateDoc(doc(db, 'agency_credit_alerts', it.id), { status, statusAt: new Date() });
    } catch (e) {
      setItems((list) => list.map((x) => (x.id === it.id ? { ...x, status: prev } : x)));
      setError(e.message || 'No se pudo cambiar el estado');
    }
  }

  async function remove(it) {
    if (!window.confirm(`¿Borrar el aviso de «${it.agencyName || it.agencyId}»?`)) return;
    try {
      await deleteDoc(doc(db, 'agency_credit_alerts', it.id));
      setItems((list) => list.filter((x) => x.id !== it.id));
    } catch (e) {
      setError(e.message || 'No se pudo borrar');
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button className="text-sm text-default-500 hover:underline" onClick={() => navigate('/agencias')}>← Agencias</button>
          <h1 className="text-2xl font-semibold">Avisos de créditos</h1>
          <p className="text-sm text-default-500">
            Agencias que han gastado el 80 % o más de los créditos de su plan asignando licencias. Buen momento para ofrecerles más créditos o un plan superior.
          </p>
        </div>
        <Button variant="flat" onPress={load} isDisabled={loading}>Recargar</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input placeholder="Buscar por agencia, ID, plan…" value={search} onValueChange={setSearch} isClearable size="sm" className="max-w-sm" />
        <Tabs size="sm" selectedKey={statusFilter} onSelectionChange={(k) => setStatusFilter(String(k))} aria-label="Filtrar por estado">
          {ALERT_STATUS.map((s) => <Tab key={s.key} title={`${s.plural} (${counts[s.key]})`} />)}
          <Tab key="all" title={`Todos (${counts.all})`} />
        </Tabs>
      </div>

      {error && <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{error}</div>}

      <Table aria-label="Avisos de créditos" removeWrapper>
        <TableHeader>
          <TableColumn>AGENCIA</TableColumn>
          <TableColumn>PLAN</TableColumn>
          <TableColumn>CRÉDITOS USADOS</TableColumn>
          <TableColumn>AVISO</TableColumn>
          <TableColumn>ÚLTIMA ASIGNACIÓN</TableColumn>
          <TableColumn>ESTADO</TableColumn>
          <TableColumn> </TableColumn>
        </TableHeader>
        <TableBody
          items={filtered}
          isLoading={loading}
          loadingContent={<Spinner />}
          emptyContent={loading ? ' ' : 'No hay avisos'}
        >
          {(it) => (
            <TableRow key={it.id}>
              <TableCell>
                <div className="flex flex-col">
                  <button className="text-left font-medium text-primary hover:underline" onClick={() => navigate(`/agencias/${it.agencyId}`)}>
                    {it.agencyName || it.agencyId}
                  </button>
                  <span className="text-xs text-default-500">{it.agencyId}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col items-start gap-1">
                  <Chip size="sm" variant="flat" color={PLAN_COLOR[it.plan] || 'default'}>{PLAN_LABEL[it.plan] || it.plan || '—'}</Chip>
                  {it.basePlan && it.basePlan !== it.plan && (
                    <span className="text-xs text-default-500">base: {PLAN_LABEL[it.basePlan] || it.basePlan}</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <span className="font-medium tabular-nums">{it.used ?? 0} / {it.cap ?? 0}</span>
                  <Chip size="sm" variant="flat" color={pctColor(it.pct || 0)}>{it.pct ?? 0} %</Chip>
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">
                <div className="flex flex-col">
                  <span>{formatTs(it.createdAt)}</span>
                  {it.triggeredByName && <span className="text-xs text-default-500">por {it.triggeredByName}</span>}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">{formatTs(it.updatedAt)}</TableCell>
              <TableCell>
                {canWrite ? (
                  <Select
                    size="sm"
                    aria-label="Estado"
                    className="w-36"
                    selectedKeys={[it.status || 'new']}
                    onSelectionChange={(keys) => setStatus(it, String(Array.from(keys)[0] || ''))}
                  >
                    {ALERT_STATUS.map((s) => <SelectItem key={s.key}>{s.label}</SelectItem>)}
                  </Select>
                ) : (
                  <Chip size="sm" variant="flat" color={STATUS_BY_KEY[it.status]?.color || 'default'}>{STATUS_BY_KEY[it.status]?.label || it.status}</Chip>
                )}
              </TableCell>
              <TableCell>
                {canWrite && <Button size="sm" variant="light" color="danger" onPress={() => remove(it)}>Borrar</Button>}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
